import { useRef, useState } from 'react'
import { X, Check, AlertTriangle, Paperclip } from 'lucide-react'
import { OverlayPortal } from '../ModalPortal'
import { useI18n } from '../../i18n'
import { useUserStore } from '../../stores'
import { supportAPI } from '../../services/api'
import { APP_VERSION } from '../../utils/appVersion'
import { apiErrorCode } from '../../utils/apiErrorMessage'
import {
  PATIENT_SUPPORT_CATEGORIES, BUG_CATEGORIES, MESSAGE_MAX, DETAIL_MAX, ATTACH_ACCEPT,
  buildBrowserFacts, describeBrowserFacts, buildTicketPayload, reportFormProblem,
  attachmentRefusal, fileReport,
} from '../../utils/bugReport'
import { OrellaSpinner } from '../OrellaLoader' // s123: the app's one spinner

/**
 * ReportProblemModal — REP-10 (+ REP-11 files). The patient's "Report a problem" form.
 *
 * Opened from Settings → Help & Support (origin 'settings') and from the crash screen
 * (origin 'crash', category preset 'App bug'). Rendered only while REPORT_PROBLEM_ENABLED is
 * on; the caller mounts it conditionally, so the facts below are computed once per opening.
 *
 * ★★★ NO IDENTITY FROM THE CLIENT. The account e-mail is SHOWN ("We'll reply to …") and never
 *   sent: orella-api reads name and e-mail from the token's users row. buildTicketPayload has no
 *   parameter that could carry them.
 * ★★ THE FACTS ARE SHOWN, AND THE LINE IS RENDERED FROM THE OBJECT THAT IS SENT.
 * ★★ SUCCESS ONLY AFTER A RESOLVED 201. 'sent' and 'partial' come from fileReport, which
 *   returns them only after submitPatientTicket resolved; a refused report keeps the form and
 *   everything typed in it.
 * ★ Nothing here logs: what she typed is free text and may hold health information.
 */
const NAVY = '#15354F'
const BLUE = '#46A8F9'

export default function ReportProblemModal({ onClose, origin = 'settings', presetCategory = '', routePattern = null }) {
  const { t, language } = useI18n()
  const email = useUserStore((state) => state.user?.email) || ''
  const [category, setCategory] = useState(presetCategory || '')
  const [message, setMessage] = useState('')
  const [steps, setSteps] = useState('')
  const [expected, setExpected] = useState('')
  const [files, setFiles] = useState([])
  const [fileNote, setFileNote] = useState('')
  const [errorKey, setErrorKey] = useState('')
  const [errorStatus, setErrorStatus] = useState(null)
  const [phase, setPhase] = useState('form') // form | sending | sent | partial
  const [result, setResult] = useState(null)
  const fileInput = useRef(null)
  const [facts] = useState(() => buildBrowserFacts({ language, appVersion: APP_VERSION, pattern: routePattern }))
  const [userAgent] = useState(() => (typeof navigator !== 'undefined' ? navigator.userAgent : ''))

  const isBug = BUG_CATEGORIES.includes(category)
  const sending = phase === 'sending'

  const close = () => {
    if (sending) return
    onClose?.()
  }

  const addFiles = (picked) => {
    let held = files
    let note = ''
    for (const file of Array.from(picked || [])) {
      const refusal = attachmentRefusal(held, file)
      if (refusal) {
        note = {
          type: 'support.attachRefusedType',
          empty: 'support.attachRefusedEmpty',
          size: 'support.attachRefusedSize',
          count: 'support.attachRefusedCount',
          total: 'support.attachRefusedTotal',
        }[refusal]
        continue
      }
      held = [...held, file]
    }
    setFiles(held)
    setFileNote(note)
    if (fileInput.current) fileInput.current.value = ''
  }

  const removeFile = (index) => {
    setFiles((list) => list.filter((_, i) => i !== index))
    setFileNote('')
  }

  const submit = async () => {
    if (sending) return
    setErrorKey('')
    setErrorStatus(null)
    const problem = reportFormProblem({ category, message, steps, expected })
    if (problem) {
      setErrorKey(problem)
      return
    }
    setPhase('sending')
    const payload = buildTicketPayload({ category, message, steps, expected, facts, origin })
    const r = await fileReport({ api: supportAPI, payload, files })
    if (r.kind === 'error') {
      setErrorKey(r.key)
      setErrorStatus(r.status)
      setPhase('form')
      return
    }
    setResult(r)
    setPhase(r.kind)
  }

  const label = { fontSize: 11, fontWeight: 600, color: NAVY, marginBottom: 4, display: 'block' }
  const field = 'w-full px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-900 focus:outline-none focus:ring-2'
  const factsLine = describeBrowserFacts(facts, userAgent)

  return (
    <OverlayPortal>
      <div className="fixed inset-0 bg-black/50 flex items-end justify-center z-[70]">
        <div
          className="bg-white w-full max-w-md rounded-t-3xl overflow-auto"
          style={{ maxHeight: '90vh', paddingBottom: 'env(safe-area-inset-bottom)' }}
        >
          <div className="px-5 py-5">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p style={{ fontSize: 15, fontWeight: 700, color: NAVY }}>{t('support.modalTitle')}</p>
                <p style={{ fontSize: 11, color: '#64748B', marginTop: 2 }}>{t('support.modalSubtitle')}</p>
              </div>
              <button
                onClick={close}
                disabled={sending}
                aria-label={t('common.close')}
                style={{ background: 'none', border: 'none', padding: 4, cursor: 'pointer' }}
              >
                <X size={18} color="#94A3B8" />
              </button>
            </div>

            {(phase === 'sent' || phase === 'partial') && result ? (
              <div className="py-4 text-center">
                {phase === 'sent' ? (
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-green-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-3">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                )}
                <p className="text-sm font-semibold text-gray-800">
                  {phase === 'sent'
                    ? (result.ticketNumber
                      ? t('support.sent', { ticket: result.ticketNumber })
                      : t('support.sentNoRef'))
                    : (result.ticketNumber
                      ? t('support.sentPartial', { ticket: result.ticketNumber, failed: result.failed, total: result.total })
                      : t('support.sentPartialNoRef', { failed: result.failed, total: result.total }))}
                </p>
                {phase === 'partial' && (result.keys || []).map((k) => (
                  <p key={k} className="text-xs text-amber-700 mt-2">{t(k)}</p>
                ))}
                {email && (
                  <p className="text-xs text-gray-500 mt-3">{t('support.replyTo', { email })}</p>
                )}
                <button
                  onClick={close}
                  className="w-full py-3 mt-5 bg-orella-500 text-white rounded-full font-semibold text-sm"
                >
                  {t('common.close')}
                </button>
              </div>
            ) : (
              <>
                <div className="mb-3">
                  <label style={label} htmlFor="report-category">{t('support.category')}</label>
                  <select
                    id="report-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    disabled={sending}
                    className={field}
                  >
                    <option value="" disabled>{t('support.categoryPlaceholder')}</option>
                    {PATIENT_SUPPORT_CATEGORIES.map((c) => (
                      <option key={c.value} value={c.value}>{t(c.labelKey)}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label style={label} htmlFor="report-message">{t('support.description')}</label>
                  <textarea
                    id="report-message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={MESSAGE_MAX}
                    rows={4}
                    disabled={sending}
                    className={field}
                  />
                  <div className="flex justify-between mt-1">
                    <span style={{ fontSize: 10, color: '#64748B' }}>{t('support.descriptionHint')}</span>
                    <span style={{ fontSize: 10, color: message.length >= MESSAGE_MAX ? '#B45309' : '#94A3B8' }}>
                      {`${message.length} / ${MESSAGE_MAX}`}
                    </span>
                  </div>
                </div>

                {isBug && (
                  <>
                    <div className="mb-3">
                      <label style={label} htmlFor="report-steps">{t('support.steps')}</label>
                      <textarea
                        id="report-steps"
                        value={steps}
                        onChange={(e) => setSteps(e.target.value)}
                        maxLength={DETAIL_MAX}
                        rows={2}
                        disabled={sending}
                        className={field}
                      />
                    </div>
                    <div className="mb-3">
                      <label style={label} htmlFor="report-expected">{t('support.expected')}</label>
                      <textarea
                        id="report-expected"
                        value={expected}
                        onChange={(e) => setExpected(e.target.value)}
                        maxLength={DETAIL_MAX}
                        rows={2}
                        disabled={sending}
                        className={field}
                      />
                    </div>
                  </>
                )}

                <div className="mb-3">
                  <button
                    onClick={() => fileInput.current?.click()}
                    disabled={sending}
                    className="flex items-center gap-1.5"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  >
                    <Paperclip size={13} color={BLUE} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: BLUE }}>{t('support.attach')}</span>
                  </button>
                  <input
                    ref={fileInput}
                    type="file"
                    accept={ATTACH_ACCEPT}
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => addFiles(e.target.files)}
                  />
                  <p style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{t('support.attachRule')}</p>
                  {files.map((f, i) => (
                    <div key={`${i}-${f.name}`} className="flex items-center justify-between mt-1.5">
                      <span className="truncate" style={{ fontSize: 11, color: '#475569', maxWidth: '80%' }}>{f.name}</span>
                      <button
                        onClick={() => removeFile(i)}
                        disabled={sending}
                        aria-label={t('common.remove')}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
                      >
                        <X size={13} color="#94A3B8" />
                      </button>
                    </div>
                  ))}
                  {fileNote && <p className="text-xs text-amber-700 mt-1">{t(fileNote)}</p>}
                </div>

                {email && (
                  <p style={{ fontSize: 10, color: '#64748B', marginBottom: 4 }}>{t('support.replyTo', { email })}</p>
                )}
                <p style={{ fontSize: 10, color: '#94A3B8', wordBreak: 'break-word' }}>
                  {t('support.attachedInfo', { facts: factsLine })}
                </p>
                <p style={{ fontSize: 10, color: '#94A3B8', marginTop: 2 }}>{t('support.attachedInfoNote')}</p>

                {errorKey && (
                  <p className="mt-3 text-sm text-red-600">{`${t(errorKey)}${apiErrorCode({ status: errorStatus })}`}</p>
                )}

                <button
                  onClick={submit}
                  disabled={sending}
                  className="w-full py-3 mt-4 bg-orella-500 text-white rounded-full font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60"
                >
                  {sending && <OrellaSpinner size={16} tone="inherit" />}
                  {sending ? t('common.sending') : t('support.send')}
                </button>
                <button
                  onClick={close}
                  disabled={sending}
                  className="w-full py-3 mt-2 bg-gray-100 rounded-full font-semibold text-sm text-gray-600 disabled:opacity-60"
                >
                  {t('common.cancel')}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </OverlayPortal>
  )
}
